import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ViolationStatement } from '../../../../models/entities/violation-statement';
import { ViolationStatus } from '../../../../models/entities/violation-status';
import { ViolationHistory } from '../../../../models/entities/violation-history';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ColumnMap } from '../../../../models/entities/column-map';
import { ViolationsService } from '../../../../services/violations/violations.service';
import { HostListenerService } from '../../../../services/host-listener.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { GrowlerService } from '../../../../services/growler.service';
import { map } from 'rxjs/operators';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { AppTableDirective, AppTableRowDirective, JustifyRightCellDirective } from '../../shared/table/table.directive';
import { AnimatedDelayDirective, AnimatedDirective } from '../../shared/directives/animated.directive';
import { AsyncPipe, NgIf } from '@angular/common';
import { LegfiDatePipeModule } from '../../../../pipes/format/legfi-date.module';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AppStatusChipComponent } from '../../shared/status-chip/status-chip.component';
import { MatListModule } from '@angular/material/list';
import { LegfiCurrencyPipeModule } from '../../../../pipes/format/legfi-currency.module';
import { MatButtonModule } from '@angular/material/button';
import { ButtonDirective } from '../../shared/directives/button.directive';
import { PreventClickDirective } from '../../shared/directives/prevent-click.directive';
import { ViolationHistoryTableDialogComponent, ViolationHistoryTableDialogInput } from './violation-history-table-dialog/violation-history-table-dialog.component';

@UntilDestroy()
@Component({
    selector: 'app-violation-history-table',
    templateUrl: './violation-history.component.html',
    standalone: true,
    styleUrls: ['./violation-history.component.scss'],
    imports: [
        MatDialogModule,
        MatTableModule,
        AppTableDirective,
        AnimatedDirective,
        AsyncPipe,
        NgIf,
        LegfiDatePipeModule,
        JustifyRightCellDirective,
        MatProgressBarModule,
        AppTableRowDirective,
        AnimatedDelayDirective,
        AppStatusChipComponent,
        MatListModule,
        LegfiCurrencyPipeModule,
        MatButtonModule,
        ButtonDirective,
        PreventClickDirective,
    ],
})
export class ViolationHistoryTableComponent implements OnChanges
{
    @Input() violation: ViolationStatement;
    @Input() statuses: ViolationStatus[];
    @Output() historyLoaded: EventEmitter<ViolationHistory[]> = new EventEmitter<ViolationHistory[]>();
    @ViewChild(MatSort) matSort: MatSort;

    dataSource: MatTableDataSource<ViolationHistory> = new MatTableDataSource<ViolationHistory>([]);
    columnMap: ColumnMap[] = [
        {key: 'mobile', title: 'Event', isMobile: true},
        {key: 'eventSummary', title: 'Event'},
        {key: 'createdAt', title: 'Date'},
        {key: 'detail', title: 'Detail'},
    ];
    isLoadingHistory = true;
    rowsAnimated = true;
    refreshingViolationHistory = false;

    constructor(
            private _violationsService: ViolationsService,
            private _host: HostListenerService,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
    }

    get columnKeys$() {
        return this._host.isMobileMode().pipe(map((isMobile) => {
            let columns: string[];
            if (isMobile) {
                columns = this.columnMap.filter((column) => !!column.isMobile).map((column) => column.key);
            } else {
                columns = this.columnMap.filter((column) => !column.isMobile).map((column) => column.key);
            }
            return columns;
        }));
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    isEventTypeWithLinkButton(history: ViolationHistory) {
        return history.eventType !== 'status' && history.eventType !== 'mail'
                && history.eventType !== 'complete' && history.eventType !== 'pending'
                && history.eventType !== 'charge';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.hasOwnProperty('violation') && this.violation) {
            this.refreshHistory();
        }
    }

    showViolationHistoryDetail(history: ViolationHistory) {
        const data: ViolationHistoryTableDialogInput = {
            history,
        };

        const panelClass = ['v2-dialog'];
        if (history.eventType !== 'email' && history.eventType !== 'comment') {
            panelClass.push('table-layout');
        }

        this._dialog.open(ViolationHistoryTableDialogComponent, {
            panelClass,
            width: MatDialogSizes.MD,
            autoFocus: 'dialog',
            data,
        });
    }

    refreshHistory() {
        this.refreshingViolationHistory = true;
        this._violationsService.getViolationHistory(this.violation.id).pipe(untilDestroyed(this)).subscribe({
            next: (history) => {
                this.dataSource.data = history;
                this.isLoadingHistory = this.refreshingViolationHistory = false;
                this.historyLoaded.emit(history);
            },
            error: () => this._growler.oops('Unable to load history for this violation'),
        });
    }
}
